import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import CheckSVG from '../../assets/check.svg';
import { COLORS } from '../../EatClubTheme';
import { isEmpty } from '../../utils/helpers';
import { Box } from '../Box';

/**
 * A checkbox :)
 */
export const Checkbox = ({
  style,
  className,
  label,
  selected, // TODO change to 'value'
  disabled,
  onChange,
  active,
  color,
  error,
  clearError,
}) => {
  const ref = useRef();

  const [hover, setHover] = useState(false);

  const toggleSelection = (e) => {
    if (!onChange) {
      return;
    }

    if (e) {
      e.stopPropagation();
    }
    onChange(!selected);
    clearError(null);
  };

  const handleKeyPress = (event) => {
    // Return early if this is not the active element
    if (document.activeElement !== ref.current) {
      return;
    }

    // Blur on escape
    if (event.key === 'Escape') {
      document.activeElement.blur();
    }

    // If press enter, select the hovered item
    if (event.key === 'Enter') {
      toggleSelection();
    }
  };

  // Add keyboard event listener to input
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  });

  const styles = {
    container: {
      ...style,
      maxWidth: '800px',
      userSelect: 'none',
      width: 'fit-content',
      // alignItems: 'center',
      ...style?.container,
    },
    active: {
      cursor: 'pointer',
      transition: 'filter 0.2s',
      filter: hover ? 'brightness(95%)' : 'none',
      ...style?.active,
    },
    disabled: {
      color: COLORS.NIMBUS,
      cursor: 'default',
      filter: hover ? 'inherit' : 'none',
      ...style?.disabled,
    },
    error: {
      border: `1px solid ${COLORS.RED}`,
      borderColor: COLORS.RED,
      ...style?.error,
    },
    checkbox: {
      alignItems: 'center',
      display: 'inline-flex',
      columnGap: '10px',
      ...style?.checkbox,
    },
    checkboxBox: {
      alignItems: 'center',
      border: '1px solid',
      borderColor: COLORS.NIMBUS,
      backgroundColor: COLORS.WHITE,
      display: 'flex',
      justifyContent: 'center',
      transition: 'border 0.1s, background-color 0.1s',
      width: '18px',
      height: '18px',
      minWidth: '18px',
      minHeight: '18px',
      borderRadius: '4px',
      ...style?.checkboxBox,
    },
    checkboxBoxDisabled: {
      borderColor: COLORS.NIMBUS,
      backgroundColor: COLORS.LINEN,
      ...style?.checkboxBoxDisabled,
    },
    checkboxBoxSelected: {
      borderColor: color || COLORS.NIMBUS,
      backgroundColor: color || COLORS.WHITE,
      ...style?.checkboxBoxSelected,
    },
    label: {
      ...style?.label,
    },
    checkMark: {
      opacity: selected ? 1 : 0,
      transition: 'opacity 0.1s',
      ...style?.checkMark,
    },
    checkMarkDisabled: {
      color: COLORS.CHARCOAL,
      ...style?.checkMarkDisabled,
    },
  };

  return (
    <Box
      className={className}
      style={{
        ...styles.container,
        ...(active && styles.active),
        ...(disabled && styles.disabled),
      }}
      onClick={(e) => !disabled && active && toggleSelection(e)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={ref}
      tabIndex={disabled ? -1 : 0}
    >
      <Box style={styles.checkbox}>
        <Box
          style={{
            ...styles.checkboxBox,
            ...(!isEmpty(error) ? styles.error : {}),
            ...(selected ? styles.checkboxBoxSelected : {}),
            ...(disabled ? styles.checkboxBoxDisabled : {}),
          }}
        >
          <CheckSVG style={{ ...styles.checkMark, ...(disabled && styles.checkMarkDisabled) }} />
        </Box>
        {label && <Box style={styles.label}>{label}</Box>}
      </Box>
      {!isEmpty(error) && <Box>{error}</Box>}
    </Box>
  );
};

Checkbox.defaultProps = {
  className: '',
  style: {},
  label: '',
  onChange: null,
  selected: false,
  disabled: false,
  active: true, // Whether the checkbox can be interacted with, or is just for show
  color: COLORS.BRAND_PRIMARY,
  error: null,
  clearError: () => {},
};

Checkbox.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  active: PropTypes.bool,
  color: PropTypes.string,
  error: PropTypes.string,
  clearError: PropTypes.func,
};
